import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inventoryApi } from '../../api/inventory-api-instance';
import { ApiError, ConflictError } from '../../api/errors';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import { SearchableSelect } from '../../components/SearchableSelect';
import {
  transferReasons,
  type Item,
  type ItemPatch,
  type Site,
  type TransferReason,
} from '../../domain/inventory';
import { sortByDisplayLabel } from '../../lib/formatting';
import { siteDisplayName, siteLabel, siteSearchAliases } from '../../lib/site-label';
import { FULL_FLEET_ITEMS_QUERY_KEY } from '../dashboard/aggregations';

const transferReasonLabels: Record<TransferReason, string> = {
  REASON_UNSPECIFIED: 'Reason not recorded',
  SITE_HARVEST: 'Site harvest',
  SCHEDULED_MAINTENANCE: 'Scheduled maintenance',
  UNIT_DAMAGE: 'Unit damage',
  UNIT_MALFUNCTION: 'Unit malfunction',
  OVERSTOCK: 'Overstock',
};
const alphabetizedTransferReasons = sortByDisplayLabel(
  transferReasons,
  (reason) => transferReasonLabels[reason],
);

interface TransferDrawerProps {
  item: Item;
  sites: Site[];
  onClose: () => void;
}

export function TransferDrawer({ item, sites, onClose }: TransferDrawerProps) {
  const queryClient = useQueryClient();
  const sortedSites = useMemo(
    () => sortByDisplayLabel(sites, (site) => siteDisplayName(site)),
    [sites],
  );
  const [destinationSiteId, setDestinationSiteId] = useState(item.locationSiteId ?? item.siteId);
  const [location, setLocation] = useState(item.location ?? '');
  const [reason, setReason] = useState<TransferReason | ''>('');
  const [trackingNumber, setTrackingNumber] = useState(item.lastTransfer?.trackingNumber ?? '');
  const [reviewing, setReviewing] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);
  const [latest, setLatest] = useState<Item | null>(null);

  const assignedSite = sites.find((site) => site.id === item.siteId);
  const currentSite = sites.find((site) => site.id === (item.locationSiteId ?? item.siteId));
  const destinationSite = sites.find((site) => site.id === destinationSiteId);

  const mutation = useMutation({
    mutationFn: ({ version }: { version: number }) => {
      const patch: ItemPatch = {
        id: item.id,
        version,
        locationSiteId: destinationSiteId,
        location: location.trim(),
        lastTransfer: {
          reason: reason || undefined,
          trackingNumber: trackingNumber.trim(),
        },
      };
      return inventoryApi.updateItem(patch);
    },
    onSuccess: async () => {
      // See EditItemDrawer's onSuccess for why the full-fleet query is
      // invalidated separately with refetchType: 'none'.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['item', item.id] }),
        queryClient.invalidateQueries({ queryKey: ['item-history', item.id] }),
        queryClient.invalidateQueries({ queryKey: ['items'] }),
      ]);
      void queryClient.invalidateQueries({
        queryKey: FULL_FLEET_ITEMS_QUERY_KEY,
        refetchType: 'none',
      });
      onClose();
    },
    onError: (error: unknown) => {
      if (error instanceof ConflictError) {
        void inventoryApi
          .getItem(item.id)
          .then(setLatest)
          .catch(() =>
            setFormError(
              'The item changed after it was loaded, and its current version could not be fetched.',
            ),
          );
        return;
      }
      if (error instanceof ApiError && error.code === 'NETWORK') {
        setUnknownOutcome(true);
        return;
      }
      setFormError(error instanceof Error ? error.message : 'The transfer could not be recorded.');
    },
  });

  function handleReview(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormError(null);
    if (!destinationSiteId) {
      setFormError('Choose the current physical destination site.');
      return;
    }
    if (!reason) {
      setFormError('Choose a transfer reason.');
      return;
    }
    setReviewing(true);
  }

  if (latest) {
    const serverSite = sites.find((site) => site.id === (latest.locationSiteId ?? latest.siteId));
    return (
      <div className="drawer-overlay" role="presentation" onClick={onClose}>
        <div
          className="drawer drawer-wide conflict-drawer"
          role="dialog"
          aria-label="Transfer version conflict"
          onClick={(event) => event.stopPropagation()}
        >
          <span className="step-label">Version conflict</span>
          <h2>{item.id} changed before transfer</h2>
          <p className="conflict-summary">
            Loaded version {item.version} · current server version {latest.version}
          </p>
          <table className="conflict-table">
            <thead>
              <tr>
                <th scope="col">Field</th>
                <th scope="col">Server now</th>
                <th scope="col">Your transfer</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Physical site</td>
                <td>{siteLabel(serverSite, latest.locationSiteId)}</td>
                <td className="conflict-draft-cell">
                  {siteLabel(destinationSite, destinationSiteId)}
                </td>
              </tr>
              <tr>
                <td>Location detail</td>
                <td>{latest.location || 'Not recorded'}</td>
                <td className="conflict-draft-cell">{location || 'Clear value'}</td>
              </tr>
            </tbody>
          </table>
          <div className="conflict-actions">
            <button type="button" onClick={onClose}>
              Discard this transfer
            </button>
            <button
              type="button"
              className="primary"
              onClick={() => {
                const version = latest.version;
                setLatest(null);
                mutation.mutate({ version });
              }}
            >
              Reapply onto v{latest.version}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      {reviewing ? (
        <div
          className="drawer drawer-wide"
          role="dialog"
          aria-label="Review transfer"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="drawer-heading">
            <div>
              <span className="step-label">Step 2 of 2</span>
              <h2>Review transfer</h2>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={onClose}
              aria-label="Close transfer drawer"
            >
              ×
            </button>
          </div>

          {formError ? (
            <p role="alert" className="form-error">
              {formError}
            </p>
          ) : null}
          {unknownOutcome ? (
            <PartialFailureNotice
              onCheckCurrentState={() => {
                void queryClient.invalidateQueries({ queryKey: ['item', item.id] });
                onClose();
              }}
            />
          ) : null}

          <div className="transfer-route" aria-label="Physical transfer route">
            <div>
              <span className="route-label">From</span>
              <strong>{siteLabel(currentSite, item.locationSiteId)}</strong>
              <small>{item.location || 'No location detail'}</small>
            </div>
            <div>
              <span className="route-label">To</span>
              <strong>{siteLabel(destinationSite, destinationSiteId)}</strong>
              <small>{location.trim() || 'No location detail'}</small>
            </div>
          </div>
          <dl className="review-grid">
            <dt>Reason</dt>
            <dd>{reason ? transferReasonLabels[reason] : 'Not selected'}</dd>
            <dt>Tracking number</dt>
            <dd>{trackingNumber.trim() || 'Not provided'}</dd>
            <dt>Assigned site</dt>
            <dd>{siteLabel(assignedSite, item.siteId)} (unchanged)</dd>
            <dt>Version</dt>
            <dd className="mono">{item.version}</dd>
          </dl>
          <div className="info-callout">
            Updates current location only. Assigned site and status stay unchanged.
          </div>
          <div className="drawer-actions">
            <button type="button" onClick={() => setReviewing(false)} disabled={mutation.isPending}>
              Back
            </button>
            <button
              type="button"
              className="primary"
              disabled={mutation.isPending}
              onClick={() => {
                setFormError(null);
                setUnknownOutcome(false);
                mutation.mutate({ version: item.version });
              }}
            >
              {mutation.isPending ? 'Recording…' : 'Confirm transfer'}
            </button>
          </div>
        </div>
      ) : (
        <form
          className="drawer drawer-wide"
          aria-label={`Transfer ${item.id}`}
          onClick={(event) => event.stopPropagation()}
          onSubmit={handleReview}
        >
          <div className="drawer-heading">
            <div>
              <span className="step-label">Step 1 of 2</span>
              <h2>Transfer {item.id}</h2>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={onClose}
              aria-label="Close transfer drawer"
            >
              ×
            </button>
          </div>
          <p className="drawer-version-note">Updates current location only.</p>

          {formError ? (
            <p role="alert" className="form-error">
              {formError}
            </p>
          ) : null}

          <label>
            Destination site
            <SearchableSelect
              ariaLabel="Destination site"
              autoFocus
              onChange={setDestinationSiteId}
              options={[
                { value: '', label: 'Select a physical destination…' },
                ...sortedSites.map((site) => ({
                  value: site.id,
                  label: siteLabel(site),
                  aliases: siteSearchAliases(site),
                })),
              ]}
              placeholder="Select a physical destination…"
              required
              value={destinationSiteId}
            />
          </label>
          <label>
            Location detail
            <input
              value={location}
              onChange={(event) => setLocation(event.target.value)}
              placeholder="Zone, cage, shelf, vessel, transit note…"
            />
          </label>
          <label>
            Transfer reason
            <SearchableSelect
              ariaLabel="Transfer reason"
              onChange={(value) => setReason(value as TransferReason | '')}
              options={[
                { value: '', label: 'Select a reason…' },
                ...alphabetizedTransferReasons
                  .filter((value) => value !== 'REASON_UNSPECIFIED')
                  .map((value) => ({
                    value,
                    label: transferReasonLabels[value],
                    aliases: [value],
                  })),
              ]}
              placeholder="Select a reason…"
              required
              value={reason}
            />
          </label>
          <label>
            Tracking number <span className="optional-label">Optional</span>
            <input
              value={trackingNumber}
              onChange={(event) => setTrackingNumber(event.target.value)}
              placeholder="Carrier or internal reference"
            />
          </label>

          <div className="info-callout">
            Current location: {siteLabel(currentSite, item.locationSiteId)}. Assigned site:{' '}
            {siteLabel(assignedSite, item.siteId)}.
          </div>

          <div className="drawer-actions">
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary">
              Review transfer
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
